import React, { useContext, useEffect } from "react";
import axios from "axios";
import RegistrationContext from "../../contexts/RegistrationContext";
import { base_url } from "../../config";
import "./style.scss";
import {
  LayoutDashboard,
  UsersIcon,
  Plane,
  GraduationCapIcon,
  NotebookText,
  Barcode,
  MonitorCheck,
  Sheet,
  Camera,
  BadgeCheck,
  Ban,
  Ticket,
  ScanLine,
  Shield,
} from "lucide-react";

/**
 * Every admin tab, keyed by the screen name the backend uses.
 *
 * Which of these a given user sees is decided server-side and arrives as
 * `admin_screens` on get_progress — see config/admin_screens.php. This object only
 * supplies what the server cannot: the icon and the label. A screen missing from here
 * still renders, with its server-side label and no icon, rather than vanishing.
 */
const SCREENS = {
  "admin-dashboard": { icon: <LayoutDashboard className="w-5 h-5" />, name: "Dashboard" },
  "admin-registrations": { icon: <UsersIcon className="w-5 h-5" />, name: "Registrations" },
  "admin-workshops": { icon: <GraduationCapIcon className="w-5 h-5" />, name: "WS & Tut" },
  "admin-codes": { icon: <Barcode className="w-5 h-5" />, name: "Gift Codes" },
  "admin-student": { icon: <GraduationCapIcon className="w-5 h-5" />, name: "Students" },
  "admin-eurai": { icon: <BadgeCheck className="w-5 h-5" />, name: "EurAI" },
  "admin-visa": { icon: <Plane className="w-5 h-5" />, name: "Visa" },
  "admin-cancellations": { icon: <Ban className="w-5 h-5" />, name: "Cancellations" },
  "admin-paper": { icon: <NotebookText className="w-5 h-5" />, name: "Papers" },
  "admin-exhibitors": { icon: <MonitorCheck className="w-5 h-5" />, name: "Exhibitors" },
  "admin-press": { icon: <Camera className="w-5 h-5" />, name: "Press" },
  "admin-social-events": { icon: <Ticket className="w-5 h-5" />, name: "Entrances" },
  "admin-access-control": { icon: <ScanLine className="w-5 h-5" />, name: "Doors" },
  "admin-exports": { icon: <Sheet className="w-5 h-5" />, name: "Exports" },
  "admin-permissions": { icon: <Shield className="w-5 h-5" />, name: "Permissions" },
};

function AdminMenu() {
  const { setError, screen, setScreen, reloadAdminBadges, adminScreens } =
    useContext(RegistrationContext);

  const [badges, setBadges] = React.useState({});

  const handler = async (step) => {
    if (step === screen) return;
    setError("");
    try {
      await axios.post(`${base_url}/api/jump`, { step });
      setScreen(step);
    } catch (error) {
      setError("Not accessible step");
    }
  };

  const getAdminBadges = async () => {
    try {
      const response = await axios.get(`${base_url}/api/admin/badges`);
      setBadges(response.data);
    } catch (error) {
      setError("Error fetching badges");
    }
  };

  useEffect(() => {
    getAdminBadges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadAdminBadges]);

  const Badge = ({ step }) => {
    const stepName = step.replace("admin-", "");
    const count = badges[stepName];
    if (!count) return null;
    return (
      <span className="admin-menu__badge">
        {count > 99 ? "99+" : count}
      </span>
    );
  };

  // No filtering here on purpose. The menu used to decide for itself which tabs to show,
  // with a different rule per tab and none at all for six of them, while the backend let
  // any admin into every screen. Both now read config/admin_screens.php.
  const menu = (adminScreens || []).map((value) => ({
    value,
    icon: SCREENS[value]?.icon ?? null,
    name: SCREENS[value]?.name ?? value.replace("admin-", ""),
  }));

  return (
    <nav className="admin-menu" aria-label="Admin sections">
      <div className="admin-menu__inner">
        {menu.map((step) => {
          const isActive = step.value === screen;
          return (
            <button
              key={step.value}
              type="button"
              onClick={() => handler(step.value)}
              aria-current={isActive ? "page" : undefined}
              className={`admin-menu__item ${
                isActive ? "admin-menu__item--active" : ""
              }`}
            >
              <span className="admin-menu__icon">
                {step.icon}
                <Badge step={step.value} />
              </span>
              <span className="admin-menu__label">{step.name}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default AdminMenu;
